import {
  PlusOutlined,
  StarFilled,
  StarOutlined
} from "@ant-design/icons"
import { Button, Flex, Form, Select, Tooltip, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { brand, neutral, semantic } from "../../../../components/widgets/tokens.ts"
import {
  REPORT_ICON_KEYS,
  ReportIconGlyph,
  type ReportIconKey
} from "../../helpers/reportIcons.tsx"
import { visibilityChoicesFor } from "../../helpers/reportVisibility.ts"
import { ReportGeneralFields } from "../ReportGeneralFields.tsx"
import type { ReportVisibility } from "../../types/types.ts"

const { Text, Title } = Typography

export interface WizardGeneralInfo {
  name: string
  description: string
  tags: string[]
  folder: string
  favorite: boolean
  visibility: ReportVisibility
  icon: ReportIconKey
}

interface ReportWizardStepGeneralProps {
  value: WizardGeneralInfo
  onChange: (patch: Partial<WizardGeneralInfo>) => void
  folderOptions: string[]
  nameError: boolean
  ownerName: string
  savedVisibility: ReportVisibility
}

// Deliberate mockup deviation (user decision 2026-07-17): no Add Widgets panel
// on this step — widgets can only be added on the Build step, so a read-only
// catalog here was confusing.
export const ReportWizardStepGeneral = ({
  value,
  onChange,
  folderOptions,
  nameError,
  ownerName,
  savedVisibility
}: ReportWizardStepGeneralProps) => {
  const { t } = useTranslation()

  return (
    <Flex gap="large" align="start" className="h-full" wrap="wrap">
      <Flex
        vertical
        gap="middle"
        style={{ width: 340, flex: "none", maxWidth: "100%" }}
      >
        <Flex vertical gap={2}>
          <Title level={4} className="m-0">
            {t("reports.wizard.general.title")}
          </Title>
          <Text type="secondary">{t("reports.wizard.general.subtitle")}</Text>
        </Flex>

        <Form layout="vertical">
          <ReportGeneralFields
            value={value}
            onChange={onChange}
            folderOptions={folderOptions}
            nameError={nameError}
          />

          <Flex
            align="center"
            justify="space-between"
            style={{ marginBottom: 12 }}
          >
            <Text>{t("reports.wizard.general.favorite")}</Text>
            <Button
              type="text"
              aria-pressed={value.favorite}
              aria-label={t("reports.wizard.general.favorite")}
              onClick={() => onChange({ favorite: !value.favorite })}
              icon={
                value.favorite ? (
                  <StarFilled style={{ color: semantic.star, fontSize: 18 }} />
                ) : (
                  <StarOutlined style={{ color: neutral.faint, fontSize: 18 }} />
                )
              }
            />
          </Flex>

          <Form.Item label={t("reports.wizard.general.owner")}>
            <Text>{ownerName}</Text>
          </Form.Item>

          <Form.Item label={t("reports.wizard.general.visibility")}>
            <Select<ReportVisibility>
              value={value.visibility}
              onChange={(visibility) => onChange({ visibility })}
              aria-label={t("reports.wizard.general.visibility")}
              options={visibilityChoicesFor(savedVisibility).map((visibility) => ({
                label: t(`reports.visibility.${visibility}`),
                value: visibility
              }))}
            />
          </Form.Item>

          <Text strong>{t("reports.wizard.general.icon")}</Text>
          <Flex gap="small" wrap="wrap" style={{ marginTop: 8 }}>
            {REPORT_ICON_KEYS.map((key) => {
              const active = value.icon === key
              return (
                <button
                  key={key}
                  type="button"
                  aria-label={key}
                  aria-pressed={active}
                  onClick={() => onChange({ icon: key })}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    color: active ? brand.teal : neutral.muted,
                    background: active ? "rgba(42,125,140,.10)" : "#fff",
                    border: `1px solid ${active ? brand.teal : neutral.border}`
                  }}
                >
                  <ReportIconGlyph iconKey={key} />
                </button>
              )
            })}
          </Flex>
          <Tooltip title={t("reports.wizard.general.uploadIconSoon")}>
            <Button type="link" icon={<PlusOutlined />} disabled className="mt-2 px-0">
              {t("reports.wizard.general.uploadIcon")}
            </Button>
          </Tooltip>
        </Form>
      </Flex>

      <Flex vertical gap="middle" className="min-w-0 flex-1">
        <Text strong>{t("reports.wizard.preview.title")}</Text>
        <Flex
          vertical
          align="center"
          justify="center"
          gap="small"
          style={{
            minHeight: 360,
            border: `1px dashed ${neutral.border}`,
            borderRadius: 12,
            background: neutral.surface,
            padding: 24,
            textAlign: "center"
          }}
        >
          <Title level={5} className="m-0">
            {t("reports.wizard.preview.ready")}
          </Title>
          <Text type="secondary">{t("reports.wizard.preview.readyHint")}</Text>
        </Flex>
      </Flex>
    </Flex>
  )
}
